import { z } from 'zod';

import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

import operatorApiFetch from './apiService';
import { productCategoryService } from './productCategoryService';

// ============================================================
// Schemas — 對齊 app/controllers/api/operator/v1/products_controller.rb
// 後端沒有 serializer，shape 由 controller 的 as_json / block 決定
// ============================================================

// `index` 端點的輕量 shape（products?search=...）
// public_photo_url 只有在請求帶 include_photo=true 時才會回傳，預設不存在
const ProductListItemSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  merchant_id: z.string(),
  validity_days: z.number().nullable(),
  manufacturing_date_required: z.boolean(),
  batch_management_required: z.boolean(),
  barcode: z.string().nullable(),
  public_photo_url: z.string().nullable().optional(),
}).passthrough();

// `get_records` 端點 — 表格用，含 stocks 聚合
const ProductStocksSchema = z.object({
  total: z.number(),
  available: z.number(),
}).passthrough();

const ProductTableRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string(),
  sku: z.string(),
  brand: z.string().nullable(),
  barcode: z.string().nullable(),
  stocks: ProductStocksSchema,
  created_at: z.string(),
  activated: z.boolean(),
  last_inbound_date: z.string().nullable(),
  last_outbound_date: z.string().nullable(),
}).passthrough();

// `show` 端點 — 詳細頁，含關聯。實際欄位非常多（Product 全 column + brand/category/merchant/default_shelf/substitutes/edit_histories + editable/deletable），
// 為避免 schema drift 與維護成本，這裡只 assert 一定有的關鍵欄位，其餘 passthrough。
const ProductDetailSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  merchant_id: z.string(),
  editable: z.boolean(),
  deletable: z.boolean(),
}).passthrough();

// `update` 端點 — product.as_json(include: :brand)
const ProductWithBrandSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  merchant_id: z.string(),
}).passthrough();

// `check_sku` — discriminated by `exists`
const CheckSkuSchema = z.discriminatedUnion('exists', [
  z.object({ exists: z.literal(true), id: z.string(), name: z.string() }),
  z.object({ exists: z.literal(false) }),
]);

// 共通：丟到背景 worker 後回 job_id
const JobIdSchema = z.object({ job_id: z.string() }).passthrough();

// `brands` 端點原始 shape — getBrands 內部用
const BrandSchema = z.object({
  id: z.string(),
  name: z.string(),
}).passthrough();

// `product_attribute_definitions` — 至少 assert id/name/label 以擋 BE shape drift；
// VerificationDrawer / ProductQuickEditDrawer 用 name 找 def、用 label 顯示 modal 文案，
// 任一欄位漂掉會 silent fallback 到 raw key（畫面變英文 snake_case），無 Sentry 訊號。
const ProductAttributeDefinitionsResponseSchema = z.object({
  data: z.array(z.object({
    id: z.string(),
    name: z.string(),
    label: z.string(),
  }).passthrough()),
}).passthrough();

// preview / replace SKU — 後端走 ProductSkuReplacementService.{preview,execute}，shape 跟 service 內部走，這裡保留 unknown
const SkuReplacementResultSchema = apiSuccess(z.unknown());

// `in_transit` 端點 — 在途庫存，shape 由 controller block 決定
const InTransitItemSchema = z
  .object({
    id: z.string(),
    inbound_id: z.string(),
    inbound_name: z.string().nullable(),
    inbound_status: z.string(),
    eta: z.string().nullable(),
    batch: z.string().nullable(),
    expiration_date: z.string().nullable(),
    scheduled_quantity: z.number(),
    putaway_quantity: z.number(),
    inventory_type: z.string().nullable(),
  })
  .passthrough();

// ============================================================
// Exported types — caller 可以 import
// ============================================================

export type ProductListItem = z.infer<typeof ProductListItemSchema>;
export type ProductTableRow = z.infer<typeof ProductTableRowSchema>;
export type ProductDetail = z.infer<typeof ProductDetailSchema>;
export type ProductWithBrand = z.infer<typeof ProductWithBrandSchema>;
export type InTransitItem = z.infer<typeof InTransitItemSchema>;

// ============================================================
// Options
// ============================================================

interface CheckSkuOptions {
  productId?: string;
  merchantId?: string;
}

interface DownloadImportTemplateBody {
  type: 'product';
  locale: string;
  merchant_id?: string;
}

// ============================================================
// Service
// ============================================================

const productService = {
  // 獲取產品列表（用於搜尋）
  fetchList: async (params: Record<string, unknown> = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const res = await operatorApiFetch(`products?${queryParams.toString()}`, {
      method: 'GET',
    });
    return apiList(ProductListItemSchema).parse(await res.json());
  },

  // 獲取單個產品
  get: async (id: string) => {
    const res = await operatorApiFetch(`products/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(ProductDetailSchema));
  },

  // 更新產品
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`products/${id}`, {
      method: 'PUT',
      body: { product: data },
    });
    return parseApiResponse(res, apiSuccess(ProductWithBrandSchema));
  },

  // 獲取產品列表（用於表格）
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('products/get_records', {
      method: 'POST',
      body: params,
    });
    return apiList(ProductTableRowSchema).parse(await res.json());
  },

  // 獲取所有產品 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('products/get_ids', {
      method: 'POST',
      body: params,
    });
    return apiSuccess(z.array(z.string())).parse(await res.json());
  },

  // 檢查品號是否可用
  // options: { productId } (編輯模式) 或 { merchantId } (新增模式)
  checkSku: async (sku: string, options: CheckSkuOptions = {}) => {
    const params = new URLSearchParams({ sku });
    if (options.productId) {
      params.append('product_id', String(options.productId));
    } else if (options.merchantId) {
      params.append('merchant_id', String(options.merchantId));
    }
    const res = await operatorApiFetch(`products/check_sku?${params.toString()}`, {
      method: 'GET',
    });
    return apiSuccess(CheckSkuSchema).parse(await res.json());
  },

  // 獲取分類選項（organization 層級，委託給 productCategoryService）
  // productCategoryService 仍是 .js，這裡 return 型別暫保持寬鬆
  getCategories: async (): Promise<Array<{ label: string; value: string }>> => {
    const data = await productCategoryService.fetchList();
    if (data.success && Array.isArray(data.data)) {
      return data.data.map((c: { name: string; id: string }) => ({ label: c.name, value: c.id }));
    }
    return [];
  },

  // 獲取品牌列表
  // options.merchantId: 多貨主場景下，商品編輯時依當前 merchant 過濾
  getBrands: async (options: { merchantId?: string } = {}) => {
    const params = new URLSearchParams();
    if (options.merchantId) params.append('merchant_id', options.merchantId);
    const qs = params.toString();
    const path = qs ? `brands?${qs}` : 'brands';
    const res = await operatorApiFetch(path, {
      method: 'GET',
    });
    const parsed = apiList(BrandSchema).parse(await res.json());
    return parsed.data.map((brand) => ({
      label: brand.name,
      value: brand.id,
      name: brand.name,
      id: brand.id,
    }));
  },

  // 批量更新
  batchUpdate: async (selectedIds: string[], actions: unknown) => {
    const res = await operatorApiFetch('products/batch_update', {
      method: 'POST',
      body: {
        ids: selectedIds,
        actions: actions,
      },
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 生成產品標籤
  generateLabel: async (productId: string, templateId: string, copies: number) => {
    const res = await operatorApiFetch(`products/${productId}/generate_label`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_id: templateId,
        copies: copies,
      }),
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 獲取產品自定義屬性定義
  getProductAttributeDefinitions: async (merchantId: string) => {
    const res = await operatorApiFetch(`product_attribute_definitions?merchant_id=${merchantId}`, {
      method: 'GET',
    });
    return ProductAttributeDefinitionsResponseSchema.parse(await res.json());
  },

  // 預覽更換 SKU
  previewReplaceSku: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('products/preview_replace_sku', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return SkuReplacementResultSchema.parse(await res.json());
  },

  // 執行更換 SKU
  replaceSku: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('products/replace_sku', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return SkuReplacementResultSchema.parse(await res.json());
  },

  // 匯出產品
  export: async (productIds: string[]) => {
    const res = await operatorApiFetch('products/export', {
      method: 'POST',
      body: { ids: productIds },
    });
    return apiSuccess(JobIdSchema).parse(await res.json());
  },

  // 獲取在途庫存
  fetchInTransit: async (id: string) => {
    const res = await operatorApiFetch(`products/${id}/in_transit`, {
      method: 'GET',
    });
    return apiSuccess(z.array(InTransitItemSchema)).parse(await res.json());
  },


  // 下載匯入模板（回傳 Excel binary，不 parse JSON）
  downloadImportTemplate: async (locale: string, merchantId?: string): Promise<Response> => {
    const body: DownloadImportTemplateBody = { type: 'product', locale };
    if (merchantId) {
      body.merchant_id = merchantId;
    }
    return operatorApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },

  // 匯入產品
  import: async (formData: FormData) => {
    const res = await operatorApiFetch('products/import', {
      method: 'POST',
      body: formData,
    });
    // import 回應 shape 因匯入結果而異（成功筆數、錯誤明細），保留寬鬆
    return apiSuccess(z.unknown()).parse(await res.json());
  },
};

export { productService };
export default productService;
